<template>
  <ion-page>
    <div class="results-container">
      <!-- Sleek Header - Matches Style -->
      <div class="overlay">
        <ion-toolbar class="sleek-header">
          <ion-buttons slot="start">
            <ion-button fill="clear" @click="openMenu()" class="menu-button">
              <ion-icon :icon="menuSharp" class="menu-icon"></ion-icon>
            </ion-button>
          </ion-buttons>
          <span class="sleek-title">Results</span>
          <ion-buttons slot="end">
            <ion-button fill="clear" @click="openFilterPopover()" class="filter-button-header">
              <ion-icon :icon="optionsOutline" class="filter-icon"></ion-icon>
            </ion-button>
          </ion-buttons>
        </ion-toolbar>

        <!-- Sticky Filter Pills -->
        <div class="sticky-filters">
          <!-- Results Count Badge -->
          <div class="results-count-pill">
            <ion-icon :icon="trophyOutline" class="pill-icon"></ion-icon>
            <span class="pill-text">{{ filteredGroups.length }} events</span>
          </div>

          <!-- Search Container -->
          <div class="search-container-sleek">
            <div class="search-input-wrapper">
              <ion-icon :icon="searchOutline" class="search-icon"></ion-icon>
              <input
                v-model="searchText"
                type="text"
                placeholder="Search events..."
                class="search-input"
                @input="handleSearch"
              />
              <ion-button
                v-if="searchText"
                fill="clear"
                @click="searchText = ''"
                class="clear-search-button"
              >
                <ion-icon :icon="closeOutline" class="clear-icon"></ion-icon>
              </ion-button>
            </div>
          </div>
        </div>
      </div>

      <!-- Active Filters Display -->
      <div class="active-filters" v-if="hasActiveFilters">
        <div class="filter-tags">
          <span class="filter-tag" v-if="selectedSportFilter !== 'all'">
            Sport: {{ formatSportName(selectedSportFilter) }}
            <button @click="setSportFilter('all')" class="remove-filter">×</button>
          </span>
          <span class="filter-tag" v-if="selectedSeasonFilter !== 'all'">
            Season: {{ selectedSeasonFilter }}
            <button @click="clearSeasonFilter()" class="remove-filter">×</button>
          </span>
          <span class="filter-tag" v-if="selectedTournamentFilter !== 'all'">
            Tournament: {{ getTournamentName(selectedTournamentFilter) }}
            <button @click="setTournamentFilter('all')" class="remove-filter">×</button>
          </span>
          <span class="filter-tag" v-if="selectedGroupNameFilter !== 'all'">
            Group: {{ selectedGroupNameFilter }}
            <button @click="setGroupNameFilter('all')" class="remove-filter">×</button>
          </span>
          <span class="filter-tag" v-if="selectedDateFilter !== 'all' || customDate">
            Date: {{ getActiveDateFilterText() }}
            <button @click="clearDateFilter" class="remove-filter">×</button>
          </span>
        </div>
      </div>

      <!-- Filter Sheet Modal - Matches Style -->
      <div
        v-if="showFilterPopover"
        class="filter-sheet-overlay"
        @click="closeFilterPopover()"
      >
        <div class="filter-sheet" @click.stop>
          <div class="filter-header">
            <h3 class="filter-title">Filters</h3>
            <ion-button fill="clear" @click="closeFilterPopover()" class="close-button">
              <ion-icon :icon="closeOutline"></ion-icon>
            </ion-button>
          </div>

          <div class="filter-content">
            <!-- Season Filter -->
            <div class="filter-section">
              <div class="filter-label">
                <ion-icon :icon="calendarOutline" class="filter-label-icon"></ion-icon>
                <span>Season</span>
              </div>
              <ion-select
                v-model="selectedSeasonFilter"
                interface="popover"
                placeholder="Select Season"
                class="filter-select"
              >
                <ion-select-option value="all">All Seasons</ion-select-option>
                <ion-select-option
                  v-for="season in availableSeasons"
                  :key="season._id"
                  :value="season._id"
                >
                  {{ season._id }}
                </ion-select-option>
              </ion-select>
            </div>

            <!-- Sport Filter -->
            <div class="filter-section">
              <div class="filter-label">
                <ion-icon :icon="fitnessOutline" class="filter-label-icon"></ion-icon>
                <span>Sport</span>
              </div>
              <ion-select
                v-model="selectedSportFilter"
                interface="popover"
                placeholder="Select Sport"
                class="filter-select"
              >
                <ion-select-option value="all">All Sports</ion-select-option>
                <ion-select-option
                  v-for="sport in availableSports"
                  :key="sport"
                  :value="sport"
                >
                  {{ formatSportName(sport) }}
                </ion-select-option>
              </ion-select>
            </div>

            <!-- Tournament Filter -->
            <div class="filter-section">
              <div class="filter-label">
                <ion-icon :icon="trophyOutline" class="filter-label-icon"></ion-icon>
                <span>Tournament</span>
              </div>
              <ion-select
                v-model="selectedTournamentFilter"
                interface="popover"
                placeholder="Select Tournament"
                class="filter-select"
              >
                <ion-select-option value="all">All Tournaments</ion-select-option>
                <ion-select-option
                  v-for="tournament in tournaments"
                  :key="tournament._id"
                  :value="tournament._id"
                >
                  {{ tournament.name }}
                </ion-select-option>
              </ion-select>
            </div>

            <!-- Result Group Filter -->
            <div class="filter-section">
              <div class="filter-label">
                <ion-icon :icon="layersOutline" class="filter-label-icon"></ion-icon>
                <span>Result Group</span>
              </div>
              <ion-select
                v-model="selectedGroupNameFilter"
                interface="popover"
                placeholder="Select Group"
                class="filter-select"
              >
                <ion-select-option value="all">All Result Groups</ion-select-option>
                <ion-select-option
                  v-for="name in availableGroupNames"
                  :key="name"
                  :value="name"
                >
                  {{ name }}
                </ion-select-option>
              </ion-select>
            </div>

            <!-- Date Filter Section -->
            <div class="filter-section">
              <div class="filter-label">
                <ion-icon :icon="calendarOutline" class="filter-label-icon"></ion-icon>
                <span>Date Range</span>
              </div>
              <div class="status-chips">
                <div
                  class="status-chip"
                  :class="{ active: selectedDateFilter === 'all' }"
                  @click="setQuickDate('all')"
                >
                  All Time
                </div>
                <div
                  class="status-chip"
                  :class="{ active: selectedDateFilter === 'today' }"
                  @click="setQuickDate('today')"
                >
                  Today
                </div>
                <div
                  class="status-chip"
                  :class="{ active: selectedDateFilter === 'week' }"
                  @click="setQuickDate('week')"
                >
                  This Week
                </div>
                <div
                  class="status-chip"
                  :class="{ active: selectedDateFilter === 'month' }"
                  @click="setQuickDate('month')"
                >
                  This Month
                </div>
              </div>
              <div class="status-chips" style="margin-top: 0.5rem;">
                <div
                  class="status-chip"
                  :class="{ active: selectedDateFilter === 'past' }"
                  @click="setQuickDate('past')"
                >
                  Past Events
                </div>
                <div
                  class="status-chip"
                  :class="{ active: selectedDateFilter === 'upcoming' }"
                  @click="setQuickDate('upcoming')"
                >
                  Upcoming
                </div>
              </div>
            </div>
          </div>

          <!-- Filter Actions -->
          <div class="filter-actions">
            <button @click="clearAllFilters" class="clear-btn">Clear All</button>
            <button @click="closeFilterPopover" class="apply-btn">Apply</button>
          </div>
        </div>
      </div>

      <!-- Results List -->
      <ion-content class="results-content" :scroll-events="true">
        <!-- Loading State -->
        <div class="loading-state" v-if="isInitialLoading">
          <div class="loading-content">
            <ion-spinner name="crescent" class="loading-spinner"></ion-spinner>
            <h3>Loading groups...</h3>
            <p>Please wait while we fetch the latest results</p>
          </div>
        </div>

        <!-- Results List - Elegant Card Style -->
        <div class="results-list" v-else-if="filteredGroups.length > 0">
          <div class="results-section">
            <div
              class="event-card-elegant"
              v-for="group in filteredGroups"
              :key="group._id"
              @click="selectGroup(group)"
            >
              <div class="card-content-elegant">
                <div class="event-icon-elegant">
                  <ion-icon :icon="getSportIcon(group.type)"></ion-icon>
                </div>

                <div class="event-info-elegant">
                  <div class="event-header-elegant">
                    <h3 class="event-title-elegant notranslate">{{ group.name || 'Unnamed Event' }}</h3>
                    <span class="status-badge-elegant" :class="getStatusClass(group.status)">
                      {{ formatStatusName(group.status) }}
                    </span>
                  </div>
                  <div class="event-meta-elegant">
                    <span class="sport-type-elegant">{{ formatSportName(group.type) }}</span>
                    <span class="separator-elegant">•</span>
                    <span class="meta-info-elegant">{{ getSportInfo(group) }}</span>
                    <template v-if="group.venue">
                      <span class="separator-elegant">•</span>
                      <span class="venue-info-elegant notranslate">{{ group.venue.name }}</span>
                    </template>
                  </div>
                  <div class="event-date-elegant" v-if="group.dateTime">
                    <ion-icon :icon="calendarOutline" class="date-icon"></ion-icon>
                    {{ formatDate(group.dateTime) }}
                  </div>
                </div>

                <div class="arrow-indicator">
                  <ion-icon :icon="chevronForwardOutline"></ion-icon>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Enhanced Empty State -->
        <div class="empty-state" v-else>
          <ion-icon :icon="searchOutline" class="empty-icon"></ion-icon>
          <h3>No events found</h3>
          <p v-if="hasActiveFilters">Try adjusting your search or filters</p>
          <p v-else>No events are currently available</p>
          <button class="clear-filters-btn" @click="clearAllFilters" v-if="hasActiveFilters">
            Clear All Filters
          </button>
        </div>
      </ion-content>
    </div>
  </ion-page>
</template>

<script>
import { defineComponent } from 'vue';
import {
  IonContent,
  IonIcon,
  IonPage,
  IonSpinner,
  IonToolbar,
  IonButtons,
  IonButton,
  IonSelect,
  IonSelectOption,
  menuController
} from '@ionic/vue';
import {
  golfOutline,
  bicycleOutline,
  tennisballOutline,
  peopleOutline,
  ribbonOutline,
  locationOutline,
  searchOutline,
  fitnessOutline,
  chevronBackOutline,
  chevronForwardOutline,
  calendarOutline,
  funnelOutline,
  menuSharp,
  optionsOutline,
  trophyOutline,
  closeOutline,
  layersOutline
} from 'ionicons/icons';
import moment from 'moment';
import _ from 'lodash';

export default defineComponent({
  name: 'ResultsList',
  components: {
    IonContent,
    IonIcon,
    IonPage,
    IonSpinner,
    IonToolbar,
    IonButtons,
    IonButton,
    IonSelect,
    IonSelectOption
  },
  data() {
    return {
      groups: [],
      tournaments: [],
      searchText: '',
      selectedSportFilter: 'all',
      selectedSeasonFilter: 'all',
      selectedTournamentFilter: 'all',
      selectedGroupNameFilter: 'all',
      selectedDateFilter: 'all',
      isInitialLoading: true,
      golfOutline,
      bicycleOutline,
      tennisballOutline,
      peopleOutline,
      ribbonOutline,
      locationOutline,
      searchOutline,
      fitnessOutline,
      chevronBackOutline,
      chevronForwardOutline,
      calendarOutline,
      funnelOutline,
      menuSharp,
      optionsOutline,
      trophyOutline,
      closeOutline,
      layersOutline,
      showDatePicker: false,
      showFilterPopover: false,
      tempCustomDate: '',
      customDate: false
    };
  },
  mounted() {
    this.initializeSeasonFilter();
    this.fetchGroups();
    this.fetchTournaments();
  },
  watch: {
    selectedSportFilter() {
      // Reset group name filter if it's no longer available
      if (this.selectedGroupNameFilter !== 'all' &&
          !this.availableGroupNames.includes(this.selectedGroupNameFilter)) {
        this.selectedGroupNameFilter = 'all';
      }
    },
    selectedTournamentFilter() {
      // Reset group name filter if it's no longer available
      if (this.selectedGroupNameFilter !== 'all' &&
          !this.availableGroupNames.includes(this.selectedGroupNameFilter)) {
        this.selectedGroupNameFilter = 'all';
      }
    },
    selectedSeasonFilter: {
      handler(newSeason, oldSeason) {
        console.log('Results: Season filter changed from', oldSeason, 'to', newSeason);
        // Store in Vuex for global season filter
        if (newSeason && newSeason !== 'all') {
          this.$store.dispatch('ui/updateSeason', { season: newSeason });
        }
        // Only fetch if changed
        if (newSeason !== oldSeason) {
          this.fetchGroups();
        }
      },
      immediate: false
    },
    // Watch for global season changes from Vuex store state directly
    '$store.state.ui.season': {
      handler(newSeason) {
        console.log('Results: Vuex season state changed to', newSeason);
        if (newSeason && newSeason !== this.selectedSeasonFilter) {
          this.selectedSeasonFilter = newSeason;
        }
      },
      immediate: true
    }
  },
  computed: {
    availableSeasons() {
      return this.$store.getters['national/seasons'] || [];
    },

    availableSports() {
      const sports = [...new Set(this.groups.map(group => group.type).filter(Boolean))];
      return sports.sort();
    },

    availableGroupNames() {
      // Start with all groups
      let filteredGroups = [...this.groups];

      // Apply sport filter if selected
      if (this.selectedSportFilter !== 'all') {
        filteredGroups = filteredGroups.filter(group => group.type === this.selectedSportFilter);
      }

      // Apply tournament filter if selected
      if (this.selectedTournamentFilter !== 'all') {
        filteredGroups = filteredGroups.filter(group => group.tournamentId === this.selectedTournamentFilter);
      }

      // Extract unique group names from filtered groups
      const groupNames = [...new Set(filteredGroups.map(group => group.name).filter(Boolean))];
      return groupNames.sort();
    },

    hasActiveFilters() {
      return this.selectedSportFilter !== 'all' ||
             this.selectedSeasonFilter !== 'all' ||
             this.selectedTournamentFilter !== 'all' ||
             this.selectedGroupNameFilter !== 'all' ||
             this.selectedDateFilter !== 'all' ||
             this.searchText.trim();
    },

    filteredGroups() {
      let filtered = [...this.groups];

      // Apply search filter
      if (this.searchText.trim()) {
        const search = this.searchText.toLowerCase();
        filtered = filtered.filter(group =>
          (group.name || '').toLowerCase().includes(search) ||
          (group.type || '').toLowerCase().includes(search) ||
          (group.venue?.name || '').toLowerCase().includes(search)
        );
      }

      // Apply sport filter
      if (this.selectedSportFilter !== 'all') {
        filtered = filtered.filter(group => group.type === this.selectedSportFilter);
      }

      // Apply season filter
      if (this.selectedSeasonFilter !== 'all') {
        filtered = filtered.filter(group => group.season === this.selectedSeasonFilter);
      }

      // Apply tournament filter
      if (this.selectedTournamentFilter !== 'all') {
        filtered = filtered.filter(group => group.tournamentId === this.selectedTournamentFilter);
      }

      // Apply group name filter
      if (this.selectedGroupNameFilter !== 'all') {
        filtered = filtered.filter(group => group.name === this.selectedGroupNameFilter);
      }

      // Apply date filter
      if (this.selectedDateFilter !== 'all') {
        const now = moment();
        filtered = filtered.filter(group => {
          if (!group.dateTime) return false;
          const eventDate = moment(parseInt(group.dateTime));

          switch (this.selectedDateFilter) {
          case 'today':
            return eventDate.isSame(now, 'day');
          case 'week':
            return eventDate.isSame(now, 'week');
          case 'month':
            return eventDate.isSame(now, 'month');
          case 'past':
            return eventDate.isBefore(now);
          case 'upcoming':
            return eventDate.isAfter(now);
          default:
            return true;
          }
        });
      }

      // Sort by order value
      return filtered.sort((a, b) => {
        const orderA = a.order || 0;
        const orderB = b.order || 0;
        return orderA - orderB;
      });
    },

    formatCurrentDateFilter() {
      const now = moment();
      switch (this.selectedDateFilter) {
      case 'today':
        return 'Today';
      case 'week':
        return `This Week (${now.format('MMM DD')} - ${now.endOf('week').format('MMM DD')})`;
      case 'month':
        return `This Month (${now.format('MMM DD')} - ${now.endOf('month').format('MMM DD')})`;
      case 'past':
        return 'Past Events';
      case 'upcoming':
        return 'Upcoming Events';
      case 'custom':
        return this.tempCustomDate ? moment(this.tempCustomDate).format('MMM DD, YYYY') : 'Custom Date';
      default:
        return 'All Time';
      }
    },

    canNavigateDate() {
      return this.selectedDateFilter === 'today' || this.selectedDateFilter === 'custom';
    },

    activeFilterCount() {
      let count = 0;
      if (this.selectedSportFilter !== 'all') count++;
      if (this.selectedSeasonFilter !== 'all') count++;
      if (this.selectedTournamentFilter !== 'all') count++;
      if (this.selectedGroupNameFilter !== 'all') count++;
      if (this.selectedDateFilter !== 'all') count++;
      if (this.searchText.trim()) count++;
      return count;
    }
  },
  methods: {
    openMenu() {
      menuController.open('side');
    },

    initializeSeasonFilter() {
      // Check for stored season in Vuex first (global season filter)
      const storedSeason = this.$store.getters['ui/season'];
      console.log('Results: Initializing season filter, stored season:', storedSeason);
      if (storedSeason) {
        this.selectedSeasonFilter = storedSeason;
        return;
      }
      // Fall back to current season from national seasons
      const seasons = this.$store.getters['national/seasons'];
      if (seasons && seasons.length > 0) {
        const currentSeason = _.find(seasons, (season) => _.get(season, 'currentSeason', false) === true);
        if (currentSeason) {
          this.selectedSeasonFilter = currentSeason._id;
          // Also store it in Vuex
          this.$store.dispatch('ui/updateSeason', { season: currentSeason._id });
        }
      }
    },

    handleSearch(event) {
      // Handle both input element events and direct value updates
      if (event && event.target) {
        this.searchText = event.target.value;
      }
    },

    setSportFilter(sport) {
      this.selectedSportFilter = sport;
    },

    setSeasonFilter(season) {
      this.selectedSeasonFilter = season;
      // Store in Vuex for global season filter
      if (season !== 'all') {
        this.$store.dispatch('ui/updateSeason', { season: season });
      }
    },

    clearSeasonFilter() {
      // Reset to default season from presentation settings or currentSeason flag
      const defaultSeason = this.$store.getters['national/defaultSeason'];
      if (defaultSeason) {
        this.selectedSeasonFilter = defaultSeason;
        this.$store.dispatch('ui/updateSeason', { season: defaultSeason });
        return;
      }
      const seasons = this.$store.getters['national/seasons'];
      const currentSeason = _.find(seasons, (season) => _.get(season, 'currentSeason', false) === true);
      if (currentSeason) {
        this.selectedSeasonFilter = currentSeason._id;
        this.$store.dispatch('ui/updateSeason', { season: currentSeason._id });
      } else {
        this.selectedSeasonFilter = 'all';
      }
    },

    setTournamentFilter(tournamentId) {
      this.selectedTournamentFilter = tournamentId;
    },

    setGroupNameFilter(groupName) {
      this.selectedGroupNameFilter = groupName;
    },

    getTournamentName(tournamentId) {
      const tournament = this.tournaments.find(t => t._id === tournamentId);
      return tournament ? tournament.name : 'Unknown Tournament';
    },

    clearAllFilters() {
      this.selectedSportFilter = 'all';
      this.selectedSeasonFilter = 'all';
      this.selectedTournamentFilter = 'all';
      this.selectedGroupNameFilter = 'all';
      this.selectedDateFilter = 'all';
      this.searchText = '';
      this.customDate = false;
      this.tempCustomDate = '';
    },

    openFilterPopover() {
      this.showFilterPopover = true;
    },

    closeFilterPopover() {
      this.showFilterPopover = false;
    },

    applyFilters() {
      this.closeFilterPopover();
    },

    formatSportName(sport) {
      if (!sport) return '';
      // Format sport names for better display
      const formatMap = {
        'Team H2H': 'Team Head-to-Head',
        'Athletics (Track)': 'Athletics',
        'Field Event': 'Field Events'
      };
      return formatMap[sport] || sport;
    },

    formatStatusName(status) {
      if (!status) return 'Unknown';
      // Capitalize and format status names
      return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    },

    formatDate(timestamp) {
      return moment(parseInt(timestamp)).format('MMM DD, YYYY');
    },

    getStatusClass(status) {
      const statusLower = (status || '').toLowerCase();
      if (statusLower.includes('completed') || statusLower.includes('finished')) return 'completed';
      if (statusLower.includes('active') || statusLower.includes('progress') || statusLower.includes('live')) return 'active';
      if (statusLower.includes('scheduled') || statusLower.includes('upcoming')) return 'scheduled';
      if (statusLower.includes('cancelled') || statusLower.includes('suspended')) return 'cancelled';
      return 'default';
    },

    getSportIcon(type) {
      switch (type) {
      case 'Golf':
        return golfOutline;
      case 'Race':
      case 'Athletics':
      case 'Athletics (Track)':
        return bicycleOutline;
      case 'Tennis':
      case 'Badminton':
        return tennisballOutline;
      case 'Team H2H':
      case 'Soccer':
      case 'Basketball':
        return peopleOutline;
      case 'Field Event':
      case 'Gymnastics':
        return ribbonOutline;
      case 'Swimming':
        return fitnessOutline;
      default:
        return ribbonOutline;
      }
    },

    async fetchGroups() {
      try {
        const params = {};
        if (this.selectedSeasonFilter !== 'all') {
          params.season = this.selectedSeasonFilter;
        }
        const response = await this.$http.get('/assemble/api/v1/livescores/result-groups', { params });
        this.groups = response.data.data || [];
      } catch (error) {
        console.error('Error fetching groups:', error);
        this.groups = [];
      } finally {
        this.isInitialLoading = false;
      }
    },

    async fetchTournaments() {
      try {
        const response = await this.$http.get('/assemble/api/v1/livescores/tournaments');
        this.tournaments = response.data.data || [];
      } catch (error) {
        console.error('Error fetching tournaments:', error);
        this.tournaments = [];
      }
    },

    getActiveDateFilterText() {
      switch (this.selectedDateFilter) {
      case 'today':
        return 'Today';
      case 'week':
        return 'This Week';
      case 'month':
        return 'This Month';
      case 'past':
        return 'Past Events';
      case 'upcoming':
        return 'Upcoming Events';
      case 'custom':
        return this.tempCustomDate ? moment(this.tempCustomDate).format('MMM DD, YYYY') : 'Custom Date';
      default:
        return 'All Time';
      }
    },

    clearDateFilter() {
      this.selectedDateFilter = 'all';
      this.customDate = false;
    },

    openDatePicker() {
      this.showDatePicker = true;
    },

    closeDatePicker() {
      this.showDatePicker = false;
    },

    selectCustomDate() {
      this.customDate = true;
    },

    clearCustomDate() {
      this.tempCustomDate = '';
      this.customDate = false;
    },

    applyCustomDate() {
      this.selectedDateFilter = 'custom';
      this.customDate = true;
      this.closeDatePicker();
    },

    handleDateFilterChange() {
      if (this.selectedDateFilter === 'custom') {
        this.openDatePicker();
      }
    },

    previousDay() {
      if (this.selectedDateFilter === 'today') {
        // Navigate to previous day logic can be added here
        console.log('Navigate to previous day');
      }
    },

    nextDay() {
      if (this.selectedDateFilter === 'today') {
        // Navigate to next day logic can be added here
        console.log('Navigate to next day');
      }
    },

    setQuickDate(filterType) {
      this.selectedDateFilter = filterType;
      this.customDate = false;
      this.tempCustomDate = '';
      this.closeDatePicker();
    },

    getSportInfo(group) {
      switch (group.type) {
      case 'Golf':
        return `${group.rounds || 1} rounds`;
      case 'Race':
      case 'Athletics':
      case 'Field Event':
      case 'Gymnastics':
        return `${(group.participants || []).length} participants`;
      case 'Team H2H':
        return `${(group.matches || []).length} matches`;
      case 'Tennis':
      case 'Badminton':
        return `${group.singlesDoubles || 'Singles'}, ${group.numberOfSets || 3} sets`;
      default:
        return `${(group.participants || []).length} participants`;
      }
    },

    selectGroup(group) {
      this.$router.push(`/results/${group._id}`);
    }
  }
});
</script>

<style lang="scss" scoped>
$background: #0f1419;

.results-container {
  height: 100vh;
  background: $background;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

// Overlay for header area
.overlay {
  background: $background;
  padding: 0;
}

// Sleek Header - Professional Glassmorphism
.sleek-header {
  --background: rgba(0, 0, 0, 0.4);
  --color: white;
  --border-width: 0;
  backdrop-filter: blur(30px) saturate(180%);
  -webkit-backdrop-filter: blur(30px) saturate(180%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  padding: 0.875rem 1rem;
  min-height: 4rem;
  position: relative;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;

  @media (max-width: 768px) {
    padding: 0.5rem 0.75rem;
    min-height: 3.5rem;
  }
}

.sleek-title {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
  font-weight: 500;
  font-size: 1.2rem;
  letter-spacing: 0.02em;
  color: rgba(255, 255, 255, 0.95);
  text-align: center;
  position: relative;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
}

.menu-button {
  --color: rgba(255, 255, 255, 0.8);
  --color-hover: rgba(255, 255, 255, 0.95);
  --background-hover: rgba(255, 255, 255, 0.08);
  --border-radius: 12px;
  height: 2.75rem;
  width: 2.75rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  margin-right: 1rem;

  &:hover {
    transform: translateY(-1px);
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.2);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 768px) {
    height: 2.5rem;
    width: 2.5rem;
  }
}

.menu-icon {
  font-size: 1.4rem;
  position: relative;
  transition: all 0.2s ease;
  color: rgba(255, 255, 255, 0.85);

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
}

.filter-button-header {
  --color: rgba(255, 255, 255, 0.8);
  --color-hover: rgba(255, 255, 255, 0.95);
  --background-hover: rgba(255, 255, 255, 0.08);
  --border-radius: 12px;
  height: 2.75rem;
  width: 2.75rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  margin-left: 1rem;

  &:hover {
    transform: translateY(-1px);
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.2);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 768px) {
    height: 2.5rem;
    width: 2.5rem;
  }
}

.filter-icon {
  font-size: 1.4rem;
  position: relative;
  transition: all 0.2s ease;
  color: rgba(255, 255, 255, 0.85);

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
}

// Sticky Filters Section
.sticky-filters {
  padding: 1rem;
  background: linear-gradient(135deg, rgba(20, 20, 22, 0.6) 0%, rgba(15, 15, 17, 0.4) 100%);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (max-width: 768px) {
    padding: 0.75rem;
    gap: 0.75rem;
  }
}

.results-count-pill {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.75rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  width: fit-content;

  .pill-icon {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.7);
  }

  .pill-text {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.75rem;
    font-weight: 600;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  }
}

// Search Container - Sleek Style
.search-container-sleek {
  width: 100%;
}

.search-input-wrapper {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 50px;
  padding: 0.75rem 1rem;
  transition: all 0.2s ease;

  &:focus-within {
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(255, 255, 255, 0.25);
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.1);
  }
}

.search-icon {
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.6);
  flex-shrink: 0;
  margin-right: 0.75rem;
}

.search-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: rgba(255, 255, 255, 0.95);
  font-size: 0.9rem;
  font-weight: 500;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
}

.clear-search-button {
  --color: rgba(255, 255, 255, 0.6);
  --background-hover: rgba(255, 255, 255, 0.1);
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  margin-left: 0.5rem;
  flex-shrink: 0;

  .clear-icon {
    font-size: 1rem;
  }
}

// Active Filters Display
.active-filters {
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.03);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);

  .filter-tags {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .filter-tag {
    padding: 0.35rem 0.6rem;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 6px;
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.7rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;

    .remove-filter {
      padding: 0;
      background: none;
      border: none;
      color: rgba(255, 255, 255, 0.5);
      font-size: 0.9rem;
      font-weight: 600;
      cursor: pointer;
      transition: color 0.2s;
      line-height: 1;

      &:hover {
        color: rgba(255, 255, 255, 0.9);
      }
    }
  }
}

// Filter Sheet Modal - Matches Style
.filter-sheet-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease;
}

.filter-sheet {
  width: 100%;
  max-height: 80vh;
  background: linear-gradient(135deg, rgba(20, 20, 22, 0.95) 0%, rgba(15, 15, 17, 0.95) 100%);
  backdrop-filter: blur(30px) saturate(180%);
  -webkit-backdrop-filter: blur(30px) saturate(180%);
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-bottom: none;
  animation: slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow-y: auto;

  @media (min-width: 768px) {
    max-width: 500px;
    margin: 0 auto;
    border-radius: 24px;
    border: 1px solid rgba(255, 255, 255, 0.15);
    max-height: 70vh;
    margin-bottom: 2rem;
  }
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 1.5rem 1rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.filter-title {
  color: white;
  font-size: 1.4rem;
  font-weight: 600;
  margin: 0;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}

.close-button {
  --color: rgba(255, 255, 255, 0.7);
  --background-hover: rgba(255, 255, 255, 0.1);
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 12px;

  ion-icon {
    font-size: 1.2rem;
  }
}

.filter-content {
  padding: 1.5rem;
}

.filter-section {
  margin-bottom: 2rem;

  &:last-child {
    margin-bottom: 0;
  }
}

.filter-label {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  color: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  font-weight: 500;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}

.filter-label-icon {
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.7);
}

.filter-select {
  --color: white !important;
  --placeholder-color: rgba(255, 255, 255, 0.6) !important;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  padding: 0.75rem 1rem;
  color: white;
  width: 100%;

  &::part(text) {
    color: white !important;
  }

  &::part(icon) {
    color: rgba(255, 255, 255, 0.6) !important;
  }
}

// Status Chips - Date Filter Style
.status-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.status-chip {
  padding: 0.75rem 1.25rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 50px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  font-weight: 500;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.25);
    color: rgba(255, 255, 255, 0.95);
    transform: translateY(-1px);
  }

  &.active {
    background: rgba(76, 175, 80, 0.2);
    border-color: rgba(76, 175, 80, 0.4);
    color: white;
    transform: translateY(-1px);
  }
}

.filter-actions {
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  .clear-btn, .apply-btn {
    flex: 1;
    padding: 1rem;
    border-radius: 12px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  }

  .clear-btn {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.15);
    color: rgba(255, 255, 255, 0.8);

    &:hover {
      background: rgba(255, 255, 255, 0.1);
      border-color: rgba(255, 255, 255, 0.25);
    }
  }

  .apply-btn {
    background: #4caf50;
    border: none;
    color: white;

    &:hover {
      background: #45a049;
      transform: translateY(-1px);
    }
  }
}

// Results Content
.results-content {
  --background: #{$background};
  height: 100%;
  overflow-y: auto;
}

.results-list {
  padding: 1rem;
  min-height: 100%;

  @media (min-width: 768px) {
    padding: 1.5rem;
  }
}

.results-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

// Elegant Event Cards - Matches Style
.event-card-elegant {
  background: linear-gradient(135deg, rgba(20, 20, 22, 0.6) 0%, rgba(15, 15, 17, 0.4) 100%);
  backdrop-filter: blur(30px) saturate(180%);
  -webkit-backdrop-filter: blur(30px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;

  &:hover {
    border-color: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(0);
  }
}

.card-content-elegant {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;

  @media (max-width: 768px) {
    padding: 0.875rem 1rem;
    gap: 0.75rem;
  }
}

.event-icon-elegant {
  flex-shrink: 0;
  width: 2.75rem;
  height: 2.75rem;
  background: #4caf50;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;

  ion-icon {
    font-size: 1.35rem;
    color: white;
  }

  @media (max-width: 768px) {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 8px;

    ion-icon {
      font-size: 1.2rem;
    }
  }
}

.event-info-elegant {
  flex: 1;
  min-width: 0;
}

.event-header-elegant {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}

.event-title-elegant {
  font-size: 1rem;
  font-weight: 600;
  color: white;
  margin: 0;
  line-height: 1.4;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
}

.status-badge-elegant {
  padding: 0.3rem 0.65rem;
  border-radius: 6px;
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  white-space: nowrap;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;

  &.completed {
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.7);
  }

  &.active {
    background: rgba(255, 255, 255, 0.15);
    color: rgba(255, 255, 255, 0.85);
  }

  &.scheduled {
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.5);
  }

  &.cancelled {
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.5);
  }

  &.default {
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.5);
  }
}

.event-meta-elegant {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.375rem;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.4;
  margin-bottom: 0.375rem;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;

  .separator-elegant {
    color: rgba(255, 255, 255, 0.3);
  }

  .sport-type-elegant {
    color: #4caf50;
    font-weight: 500;
  }

  .meta-info-elegant, .venue-info-elegant {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.event-date-elegant {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;

  .date-icon {
    font-size: 0.875rem;
    color: rgba(255, 255, 255, 0.4);
  }
}

.arrow-indicator {
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.3);
  transition: all 0.2s ease;

  ion-icon {
    font-size: 1.25rem;
  }

  .event-card-elegant:hover & {
    color: rgba(255, 255, 255, 0.6);
    transform: translateX(4px);
  }
}

// Loading State - Elegant Style
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  min-height: 400px;

  .loading-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;
  }

  .loading-spinner {
    --color: #4caf50;
    width: 3rem;
    height: 3rem;
  }

  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
    margin: 0;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  }

  p {
    color: rgba(255, 255, 255, 0.5);
    margin: 0;
    font-size: 0.9rem;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  }
}

// Empty State - Elegant Style
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;

  .empty-icon {
    font-size: 4rem;
    color: rgba(255, 255, 255, 0.2);
    margin-bottom: 1.5rem;
  }

  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
    margin: 0 0 0.5rem 0;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  }

  p {
    color: rgba(255, 255, 255, 0.5);
    margin: 0;
    font-size: 0.9rem;
    max-width: 300px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  }

  .clear-filters-btn {
    margin-top: 1.5rem;
    padding: 0.75rem 1.5rem;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 50px;
    color: rgba(255, 255, 255, 0.85);
    font-size: 0.9rem;
    font-weight: 500;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.12);
      border-color: rgba(255, 255, 255, 0.25);
      color: white;
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
    }
  }
}

// Ion Select Global Styles
ion-select {
  --color: white !important;
  --placeholder-color: rgba(255, 255, 255, 0.6) !important;
  color: white !important;

  &::part(text) {
    color: white !important;
  }

  &::part(icon) {
    color: rgba(255, 255, 255, 0.6) !important;
  }
}

ion-select-option {
  --color: white !important;
}

ion-toolbar,
ion-item {
  --border-width: 0 !important;
  --background: #{$background};
  --color: white;
}
</style>